import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { createCommunitySchema, topicSchema, type CommunityDetail } from "@atrium/shared";
import { ApiError, api, errorMessage } from "../../lib/api";
import { useUI } from "../../lib/uiStore";
import { Dialog } from "../../components/ui/Dialog";
import { useImageEditor } from "../../components/ImageEditor";
import { Button } from "../../components/ui/Button";
import { Field, Input, Textarea } from "../../components/ui/Input";
import { Segmented } from "../../components/ui/Controls";
import { CommunityIcon } from "../../components/ui/Avatar";
import { IconCamera, IconGlobe, IconLock, IconX } from "../../components/icons";
import { toast } from "../../components/ui/Toast";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);

export function TopicInput({ value, onChange, max = 5 }: { value: string[]; onChange: (v: string[]) => void; max?: number }) {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const add = () => {
    const t = draft.trim().replace(/^#/, "").toLowerCase();
    if (!t) return;
    if (!topicSchema.safeParse(t).success) return setErr("Topics start with a letter: letters, numbers, underscores");
    if (value.includes(t)) return setDraft("");
    if (value.length >= max) return setErr(`Up to ${max} topics`);
    onChange([...value, t]);
    setDraft("");
    setErr(null);
  };
  return (
    <div>
      <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-line-strong bg-surface px-2 py-1.5 focus-within:border-accent">
        {value.map((t) => (
          <span key={t} className="inline-flex h-7 items-center gap-1 rounded-md bg-surface-3 pl-2 pr-1 text-sm text-fg">
            #{t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))} className="rounded p-0.5 text-fg-3 hover:text-fg">
              <IconX className="icon size-3.5" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === " ") {
              e.preventDefault();
              add();
            } else if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={add}
          placeholder={value.length ? "" : "design, music, gaming"}
          aria-label="Add topic"
          className="h-7 min-w-24 flex-1 bg-transparent px-1 text-base text-fg placeholder:text-fg-3 focus:outline-none"
        />
      </div>
      {err && <p className="mt-1 text-xs text-danger">{err}</p>}
    </div>
  );
}

export function ImagePicker({
  value,
  onChange,
  purpose,
  name,
  shape = "community",
}: {
  value: { id: string; url: string } | null;
  onChange: (v: { id: string; url: string } | null) => void;
  purpose: "community_icon" | "avatar";
  name: string;
  shape?: "community" | "user";
}) {
  // Every pick goes through the shared editor so the image can be framed and zoomed.
  const editor = useImageEditor(purpose, onChange);
  return (
    <div className="flex items-center gap-4">
      <button type="button" onClick={editor.pick} className="group relative" aria-label="Choose icon">
        <CommunityIcon src={value?.url} name={name || "?"} size="xl" className={shape === "user" ? "!rounded-full" : ""} />
        <span className="absolute inset-0 flex items-center justify-center rounded-[18px] bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <IconCamera className="icon" />
        </span>
      </button>
      <div className="flex gap-2">
        <Button size="sm" onClick={editor.pick}>
          Upload
        </Button>
        {value && (
          <>
            <Button size="sm" variant="ghost" onClick={() => editor.adjust(value.url)}>
              Adjust
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
              Remove
            </Button>
          </>
        )}
      </div>
      {editor.dialog}
    </div>
  );
}

export function CreateCommunityDialog() {
  const { createCommunityOpen: open, setCreateCommunityOpen } = useUI();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [topics, setTopics] = useState<string[]>([]);
  const [icon, setIcon] = useState<{ id: string; url: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setDescription("");
      setVisibility("public");
      setTopics([]);
      setIcon(null);
      setErrors({});
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = { name, slug: slug || slugify(name), description, visibility, topics, iconMediaId: icon?.id ?? null };
    const parsed = createCommunitySchema.safeParse(body);
    if (!parsed.success) {
      const f: Record<string, string> = {};
      for (const i of parsed.error.issues) f[String(i.path[0])] ??= i.message;
      return setErrors(f);
    }
    setPending(true);
    try {
      const c = await api.post<CommunityDetail>("/api/communities", parsed.data);
      await qc.invalidateQueries({ queryKey: ["my-communities"] });
      setCreateCommunityOpen(false);
      navigate(`/c/${c.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else toast.error(errorMessage(err, "Couldn't create the community"));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={setCreateCommunityOpen}
      title="Create a community"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => setCreateCommunityOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="create-community" loading={pending}>
            Create
          </Button>
        </>
      }
    >
      <form id="create-community" onSubmit={submit} className="space-y-4" noValidate>
        <ImagePicker value={icon} onChange={setIcon} purpose="community_icon" name={name} />
        <Field label="Name" error={errors.name}>
          {(p) => (
            <Input
              {...p}
              value={name}
              maxLength={50}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(slugify(e.target.value));
              }}
            />
          )}
        </Field>
        <Field label="Address" error={errors.slug}>
          {(p) => (
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-fg-3">+</span>
              <Input
                {...p}
                className="pl-7"
                value={slug}
                maxLength={32}
                spellCheck={false}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                }}
              />
            </div>
          )}
        </Field>
        <Field label="Description" error={errors.description} counter={`${description.length}/500`}>
          {(p) => <Textarea {...p} value={description} maxLength={500} rows={3} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <Field label="Topics" error={errors.topics}>
          {() => <TopicInput value={topics} onChange={setTopics} />}
        </Field>
        <div className="space-y-2">
          <div className="text-sm font-medium text-fg-2">Visibility</div>
          <Segmented
            ariaLabel="Visibility"
            value={visibility}
            onChange={setVisibility}
            className="w-full"
            options={[
              { value: "public", label: "Public", icon: IconGlobe },
              { value: "private", label: "Private", icon: IconLock },
            ]}
          />
          <p className="text-xs text-fg-3">
            {visibility === "public" ? "Anyone can find, follow and join. Announcements can be published to the feed." : "Invite only. Hidden from discovery, profiles and the feed."}
          </p>
        </div>
      </form>
    </Dialog>
  );
}
